import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import axios from 'axios'
import API from '../API/API'
import { apiHost } from '../config/config'
import { useAlerts } from './AlertContext'

interface User {
  id: string
  username: string
  roles: string[]
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  login: (username: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const { addAlert } = useAlerts()

  // Helper function to check if error is connection refused
  const isConnectionRefused = (error: any): boolean => {
    return error.code === 'ERR_CONNECTION_REFUSED' || 
           error.code === 'ERR_NETWORK' ||
           error.message?.includes('ERR_CONNECTION_REFUSED') ||
           error.message?.includes('Network Error') ||
           error.message?.includes('connect ECONNREFUSED')
  }
  // Logout function
  const logout = async (): Promise<void> => {
    console.log('AuthContext: Logout triggered')
    try {
      // Call API logout endpoint
      await API.logout()
    } catch (error) {
      console.error('Logout API call failed:', error)
      // Continue with local logout even if API call fails
    }

    // Remove token from localStorage
    localStorage.removeItem('auth_token')
    API.token = ''

    // Update state
    setUser(null)
    setIsAuthenticated(false)
    
    console.log('AuthContext: User logged out, authentication state cleared')
  }

  // Initialize API with host and set global logout handler
  useEffect(() => {
    API.init(apiHost)
    API.setLogoutHandler(logout)
    console.log('API initialized with host:', apiHost)
  }, [])
  
  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkAuthStatus = async (): Promise<void> => {
      const token = localStorage.getItem('auth_token')

      if (token == null) {
        setIsLoading(false)
        return
      }

      try {
        // Set the token in the API instance
        API.token = token

        // Validate token and get user data using WhoAmI
        const userResponse = await API.WhoAmI()

        if (userResponse) {
          setUser({
            id: userResponse.id || 'unknown',
            username: userResponse.username || 'unknown',
            roles: userResponse.roles || []
          })
          setIsAuthenticated(true)
        } else {
          // Invalid token
          localStorage.removeItem('auth_token')
          API.token = ''
        }
    } catch (error) {
        // Token validation failed
        console.error('Auth token validation failed:', error)
        
        // Check for connection refused errors and show user-facing message
        if (isConnectionRefused(error)) {
          addAlert('error', 'The Insite Server cannot be reached')
        }
        
        localStorage.removeItem('auth_token')
        API.token = ''
      } finally {
        setIsLoading(false)
      }
    }

    let isMounted = true
    const runCheck = async () => {
      if (isMounted) {
        await checkAuthStatus()
      }
    }
    
    void runCheck()
    
    return () => {
      isMounted = false
    }
  }, [])

  // Login function
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await API.login(username, password)

      console.log('Login response:', JSON.stringify(response))

      // The API.login method stores the token in the API instance and returns the response
      if (response.data?.token) {
        const token = response.data.token

        // Store token in localStorage for persistence
        localStorage.setItem('auth_token', token)

        // Get user info using the WhoAmI endpoint
        try {
          const userResponse = await API.WhoAmI()
          setUser({
            id: userResponse.id || username,
            username: userResponse.username || username,
            roles: userResponse.roles || []
          })
        } catch (error) {
          // Fallback if WhoAmI fails
          setUser({
            id: username,
            username: username,
            roles: []
          })
        }

        setIsAuthenticated(true)
        return true
      }

      // If no token in response, it's an authentication failure
      addAlert('error', 'Invalid username or password')
      return false
    } catch (error) {
      console.error('Login error:', error)
      
      // Check for connection refused errors
      if (isConnectionRefused(error)) {
        addAlert('error', 'The Insite Server cannot be reached')
      } else {
        // Check for specific authentication errors
        const errorMessage = (error as any)?.response?.data?.message || (error as any)?.message
        if (errorMessage && errorMessage.toLowerCase().includes('unauthorized')) {
          addAlert('error', 'Invalid username or password')
        } else {
          addAlert('error', 'Login failed. Please try again.')
        }
      }
      return false
    }
  }

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
